// src/modules/app-views/account/pages/Init/tabs/MyProfileTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import { Card } from 'antd';
import translateX from 'utils/translateX';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import SimpleEntityForm from 'components/SimpleComponents/SimpleEntityForm/SimpleEntityForm';
import accountService from '../../../service';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import authService from 'services/authService';
import attachmentsService from 'services/attachmentsService';
import appService from 'services/appService';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import useZustand from 'hooks/useZustand';
import useApi from 'hooks/useApi';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { mergeUrlParamsQuery } from 'utils/urlUtils';
import { API } from 'constants/Api';
import { buildInitialValues } from 'utils/mock';
import CardTitleWithFlag from 'components/ui/CardTitleWithFlag';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const isMockStep = false;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const AccountsMyProfileTab = ({ tabName }) => {

  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const storedUser = useZustand('auth', 'user');
  const { viewMode, data, tab: selectedTab } = useDocumentContext();

  const { put, loading } = useApi({
    hideLoading: selectedTab === tabName,
    paramKey: 'userId',
    selectedTab: tabName,
    autoFetch: true,
  });

  // 2. HOOKS & REFS
  const isInitializedRef = useRef(false);

  // 3. LOCAL VARIABLES I
  const locale = appService.getLocale();
  const requiredFields = [
    'countryCode',

    'cpf.value',
    'name',
    'fullName',
    'phone.value',
    'email.value',
    'birthdate',
    'gender',
    'address.zip',
    'address.number',

    'firstName',
    'lastName',

    'address.countryCode',
  ];
  if (locale !== 'BR') {
    requiredFields.push('address.address1', 'address.region', 'address.city');
  }

  // 4. LOCAL STATE
  const [initialValues] = useState(() => buildInitialValues(isMockStep));
  const [disabledFields, setDisabledFields] = useState(['countryCode', 'email.value']);
  const [state, setState] = useState({
    dataOrigin: 'new_data',
    entityType: 'person',
  });

  // 5. EFFECTS
  useEffect(() => {
    const df = [];
    if (data?.phone?.isVerified) df.push('phone.value');
    setDisabledFields(p => [...p, ...df]);
  }, [data?.phone]);

  // 6. HANDLERS
  const handleFinish = useCallback(
    async ({ values, allValues }) => {
      let dataSet = { ...values };

      if (values?.avatar?.attachmentId) {
        dataSet.avatar = {
          attachmentId: values.avatar.attachmentId,
          thumbUrl: values.avatar.thumbUrl || values.avatar.largeUrl,
          storageUrl: values.avatar.largeUrl || values.avatar.thumbUrl,
        };
      } else if (dataSet.avatar?.base64Thumb) {
        const { base64Thumb, previewUrl, isUploading, ...cleanAvatar } = dataSet.avatar;
        dataSet.avatar = cleanAvatar;
      }

      const result = await put({
        url: mergeUrlParamsQuery({
          url: API.ACCOUNTS.URL,
          params: { _id: storedUser._id },
        }),
        dataSet,
      });

      const nextStep = 'complete_your_profile';
      if (
        (values.countryCode === 'BR' && values?.cpf) ||
        (values.countryCode !== 'BR' && !accountService.hasNextStep(nextStep))
      ) {
        await accountService.putNextStep(nextStep);
      } else {
        await authService.signInRefresh();
      }
    },
    [data, storedUser, state, put]
  );

  // 7. LOCAL VARIABLES II

  // 8. UI
  return (
    <div className="card-container">
      <div className="container mt-5">
        <SimpleFormProvider
          onFinish={handleFinish}
          showSubmitButton={false}
          initFocus={locale === 'BR' ? 'cpf.value' : 'firstName'}
          additionalValues={{
            entityProfile: 'person',
          }}
          ignoreResetFormOnSubmit={false}
          requiredFields={requiredFields}
          disabledFields={disabledFields}
          locked={viewMode === 'view'}
          showInfoMessage={false}
          forceViewAfterSave={true}
          initialValues={initialValues}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div style={{ position: 'relative' }}>
            <CardHeaderWithCountry showAvatar locate="tab" data={data} watchField="countryCode" hideHome />
          </div>

          <Card
            title={
              <CardTitleWithFlag
                title={translateX('personal_data')}
                watchField="countryCode"
                emoji={false}
                flagSize={32}
              />
            }
          >
            <SimpleLoadingArea name="userData" isFetching={loading} data={data} />
            <SimpleEntityForm
              setState={setState}
              state={state}
              selectable={false}
              dataOrigin={state.dataOrigin}
              showAdditionalInfoForm={true}
              showAddressForm={true}
              entityTypeModel={state.entityType}
              entityType={state.entityType}
              searchCpfByApi={false}
              hideAddressCountrySelector={true}
              personPhoneProfile="mobile"
              showNote={false}
            />
          </Card>
        </SimpleFormProvider>
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default AccountsMyProfileTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
